import { useEffect } from "react"
import { isEnum } from "utils/isEnum"
import { parseEnum } from "utils/parseEnum"

import { USER_LOCALE_KEY, storageEventKey } from "@shared/constants/storage"
import { Locale } from "@shared/enums/general"

import type { StorageSerializer } from "./storage"
import { localStorageStrategy } from "./storage"
import { useLocalState } from "./useLocalState"

/**
 * Query parameter that sets the locale, e.g. `?lang=en-GB`. Lets us hand out
 * links that open the site in a specific language.
 */
export const LOCALE_QUERY_PARAM = "lang"

/**
 * Determine a sensible default locale from the browser's own language
 * preferences. Dutch is preferred whenever it appears anywhere in the
 * browser's language list — English is only used as a last resort, when
 * Dutch isn't listed at all.
 */
export function detectBrowserLocale(): Locale {
  const languages =
    typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]

  const isDutch = languages.some((language) => language.toLowerCase().startsWith("nl"))
  return isDutch ? Locale.Nl : Locale.En
}

/**
 * `USER_LOCALE_KEY` is stored as a raw enum string (e.g. `nl-NL`), not JSON —
 * {@link storeLocale} writes it directly and {@link getCurrentLocale} reads it
 * outside React. Use the same raw format here instead of the default JSON serializer.
 *
 * `from` also accepts the legacy JSON-quoted format (e.g. `"nl-NL"`) this hook
 * used to write via the default JSON serializer, so a locale a user picked
 * via `LanguageSwitch` before this fix still deserializes correctly instead
 * of silently reverting to the default locale.
 */
const rawLocaleSerializer: StorageSerializer<Locale> = {
  to: (value) => value,
  from: (value) => {
    if (isEnum(value, Locale)) {
      return value
    }

    try {
      return parseEnum(JSON.parse(value), Locale, detectBrowserLocale())
    } catch {
      return detectBrowserLocale()
    }
  },
}

/**
 * Gets the current locale from localStorage, falling back to the
 * browser-detected default. For code outside React (the translation helpers)
 * that cannot call {@link useLocale}; parses through
 * {@link rawLocaleSerializer} so it agrees with the hook on legacy values, and
 * like `useStorage` falls back instead of throwing when storage is blocked.
 */
export function getCurrentLocale(): Locale {
  try {
    const stored = localStorageStrategy.getItem(USER_LOCALE_KEY)
    return stored === undefined ? detectBrowserLocale() : rawLocaleSerializer.from(stored)
  } catch (error) {
    console.warn(`Error reading storage value for key "${USER_LOCALE_KEY}":`, error)
    return detectBrowserLocale()
  }
}

/**
 * Read the locale from a URL query string (see {@link LOCALE_QUERY_PARAM}).
 *
 * Both the full locale (`en-GB`) and a bare language code (`en`, `en-US`) are
 * accepted, case-insensitively, so links can be written in whichever form is
 * most natural. Matching is on the language subtag, so a value that merely
 * starts with the same two letters (`enormous`) is not a match. Unknown values
 * yield `undefined` so the locale falls back to the stored preference or
 * {@link detectBrowserLocale} instead of erroring.
 */
export function localeFromQueryString(search: string): Locale | undefined {
  const requested = new URLSearchParams(search).get(LOCALE_QUERY_PARAM)?.toLowerCase()

  if (!requested) {
    return undefined
  }

  const [language] = requested.split("-")

  return Object.values(Locale).find((locale) => locale.slice(0, 2).toLowerCase() === language)
}

/**
 * Persist the locale and notify every mounted `useLocale` consumer.
 *
 * Writing to `localStorage` directly would leave the already rendered
 * `IntlProvider` on the previous language until a full page reload, because
 * `useStorage` only learns about same-tab writes through its custom event.
 */
export function storeLocale(locale: Locale): void {
  localStorageStrategy.setItem(USER_LOCALE_KEY, rawLocaleSerializer.to(locale))
  window.dispatchEvent(new CustomEvent(storageEventKey(USER_LOCALE_KEY), { detail: locale }))
}

/**
 * Apply the `?lang=` locale from the current URL, if any.
 *
 * Called once from `main.tsx` before React renders, so the first paint is
 * already in the requested language and no router or component needs to know
 * about the parameter. An explicit locale in the URL takes precedence over a
 * stored preference, and is persisted so it survives navigation to pages
 * without the parameter.
 */
export function initializeLocale(): void {
  const requested = localeFromQueryString(window.location.search)

  if (requested) {
    storeLocale(requested)
  }
}

/**
 * Keep the document's own language markers in step with the locale, so Google
 * Translate (and screen readers) pick up the language we are actually rendering.
 */
function useDocumentLanguage(locale: Locale): void {
  useEffect(() => {
    const language = locale.substring(0, 2)
    document.documentElement.lang = language
    document.querySelector("meta[http-equiv='Content-Language']")?.setAttribute("content", language)
  }, [locale])
}

/**
 * Get access to the current locale of the website. Defaults to the
 * browser's language (see {@link detectBrowserLocale}).
 */
export function useLocale(): Locale {
  const [locale] = useLocalState(USER_LOCALE_KEY, detectBrowserLocale(), rawLocaleSerializer)
  useDocumentLanguage(locale)

  return locale
}

/**
 * Hook to get both locale value and setter function. Defaults to the
 * browser's language (see {@link detectBrowserLocale}).
 */
export function useLocaleWithSetter(): { locale: Locale; setLocale: (locale: Locale) => void } {
  const [locale, setLocale] = useLocalState(USER_LOCALE_KEY, detectBrowserLocale(), rawLocaleSerializer)
  useDocumentLanguage(locale)

  return { locale, setLocale }
}
